import { Component, Input } from '@angular/core';

import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';

import { BriefingsApiErrorEnum } from '../../../../api/briefings/enum/briefings-api-error.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { validateEmail } from '../../../Misc/helpers/formatting/email.helper';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';

import { RecipientModel } from '../../models/recipient.model';
import { MailingListsService } from '../../services/mailing-lists.service';

@Component({
    selector: 'agpr-edit-recipient-dialog',
    templateUrl: './edit-recipient-dialog.component.html',
    styleUrls: ['./edit-recipient-dialog.component.scss'],
    standalone: false
})
export class EditRecipientDialogComponent extends AbstractDialogComponent {
    readonly buttonTypes = ButtonTypesEnum;
    isCreate: boolean = true;
    loading: boolean = false;
    value: RecipientModel = new RecipientModel();

    @Input() set recipient(recipient: RecipientModel) {
        this.isCreate = !recipient;
        this.value = recipient ? recipient.clone() : new RecipientModel();
    }

    emailFormatInvalid: boolean = false;

    constructor(
        public coreDialogService: CoreDialogService,
        private mailingListsService: MailingListsService,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'EditRecipient');
    }

    validateEmailFormat(): void {
        this.value.email = this.value.email.trim();
        this.emailFormatInvalid = !validateEmail(this.value.email);
    }

    /**
     * Action when the user clicks the "OK" button
     */
    ok(): void {
        // do validations here
        this.validateEmailFormat();

        if (this.emailFormatInvalid) {
            return;
        }

        let request: Observable<RecipientModel>;
        const growlPrefix = this.isCreate ? 'Common.mailingLists.editRecipientDialog.create' : 'Common.mailingLists.editRecipientDialog.edit';
        this.loading = true;
        if (this.isCreate) {
            request = this.mailingListsService.createRecipient(this.value);
        } else {
            request = this.mailingListsService.updateRecipient(this.value);
        }
        request.subscribe({
            next: (recipient) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: `${growlPrefix}.success.heading`,
                    detail: this.localizationService.transform(`${growlPrefix}.success.message`,
                        { name: recipient.name || recipient.email }
                    )
                });
                this.closeDialog(recipient);
            },
            error: (err) => {
                switch (err?.error?.details?.[0]?.code) {
                    case BriefingsApiErrorEnum.recipientEmailDuplicate:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: `${growlPrefix}.error.heading`,
                            detail: this.localizationService.transform(`${growlPrefix}.error.message.duplicate`,
                                { email: this.value.email }
                            )
                        });
                        break;
                    case BriefingsApiErrorEnum.invalidEmail:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: `${growlPrefix}.error.heading`,
                            detail: this.localizationService.transform(
                                'Common.mailingLists.editRecipientDialog.error.message.invalidEmail',
                                { email: this.value.email }
                            )
                        });
                        break;
                    default:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: `${growlPrefix}.error.heading`,
                            detail: this.localizationService.transform(`${growlPrefix}.error.message`,
                                { name: this.value.name || this.value.email }
                            )
                        });
                        break;
                }
                this.loading = false;
            }
        });
    }

    /**
     * Action when the user clicks the "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.closeDialog();
    }
}
